import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (fullName: string) => Promise<{ error: any }>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<{ error: any }>;
  updateEmail: (newEmail: string, password: string) => Promise<{ error: any }>;
  getMFAStatus: () => Promise<{ enabled: boolean; error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Validate required environment variables
if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY) {
  throw new Error('Missing required Supabase environment variables');
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer admin check to avoid blocking
          setTimeout(() => {
            checkAdminRole(session.user);
          }, 0);
        } else {
          setIsAdmin(false);
          setLoading(false);
        }
      }
    );

    // Check for existing session with validity verification
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        // Verify session is still valid
        supabase.auth.getUser().then(({ data: { user }, error }) => {
          if (error || !user) {
            // Session is invalid, force clear
            supabase.auth.signOut();
            setUser(null);
            setIsAdmin(false);
            setLoading(false);
          } else {
            setUser(user);
            checkAdminRole(user);
          }
        });
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminRole = async (user: User) => {
    try {
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'admin'
      });
      
      if (error) {
        console.error('Error checking admin role:', error);
        setIsAdmin(false);
      } else {
        setIsAdmin(data === true);
      }
    } catch (error) {
      console.error('Error checking admin role:', error);
      setIsAdmin(false);
    }
    setLoading(false);
  };

  const signIn = async (email: string, password: string) => {
    try {
      // Add timing delay to prevent timing attacks
      const startTime = Date.now();
      const randomDelay = () => {
        const elapsed = Date.now() - startTime;
        const targetTime = 1000 + Math.random() * 500; // 1-1.5 seconds
        const remaining = Math.max(0, targetTime - elapsed);
        return new Promise(resolve => setTimeout(resolve, remaining));
      };

      // Check server-side rate limiting
      const rateLimitResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-login-rate-limit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ email: email.toLowerCase() }),
        }
      );

      const rateLimitData = await rateLimitResponse.json();

      if (rateLimitData.is_locked) {
        await randomDelay();
        
        toast({
          title: "Login Failed",
          description: "Invalid credentials. Please try again.",
          variant: "destructive",
        });
        
        return { error: new Error('Authentication failed') };
      }

      // Validate email domain
      if (!email.endsWith('@4fox.uk')) {
        // Log failed attempt
        await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-login-attempt`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
            body: JSON.stringify({ email: email.toLowerCase(), success: false }),
          }
        );

        await randomDelay();
        toast({
          title: "Login Failed",
          description: "Invalid credentials. Please try again.",
          variant: "destructive",
        });
        return { error: new Error('Authentication failed') };
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        // Log failed attempt
        await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-login-attempt`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
            body: JSON.stringify({ email: email.toLowerCase(), success: false }),
          }
        );

        toast({
          title: "Access Denied",
          description: "Invalid credentials",
          variant: "destructive",
        });
        return { error };
      }

      // Check admin role immediately
      await checkAdminRole(data.user);

      // Log successful login
      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-login-attempt`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ email: email.toLowerCase(), success: true }),
        }
      );
      
      return { error: null };
    } catch (error: any) {
      toast({
        title: "Access Denied",
        description: "Invalid credentials",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      // Validate email domain
      if (!email.endsWith('@4fox.uk')) {
        const domainError = new Error('Access Denied');
        toast({
          title: "Access Denied",
          description: "Invalid credentials",
          variant: "destructive",
        });
        return { error: domainError };
      }

      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
          }
        }
      });

      if (error) {
        toast({
          title: "Access Denied",
          description: "Unable to create account",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Account created!",
          description: "You have been logged in successfully.",
        });
      }

      return { error };
    } catch (error: any) {
      toast({
        title: "Access Denied",
        description: "Unable to create account",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Get current session first
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // No active session, just clear local state
        setUser(null);
        setIsAdmin(false);
        return;
      }
      
      // Attempt global sign out
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      
      if (error && error.message !== 'Session not found') {
        throw error;
      }
      
      // Always clear local state
      setUser(null);
      setIsAdmin(false);
      
      toast({
        title: "Logged out",
        description: "You have been logged out successfully.",
      });
    } catch (error: any) {
      console.error('Logout error:', error);
      
      // CRITICAL: Force clear sessionStorage to remove stale session
      sessionStorage.removeItem(`sb-${import.meta.env.VITE_SUPABASE_PROJECT_ID}-auth-token`);
      
      // Clear all Supabase-related keys from sessionStorage
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('sb-') && key.includes('-auth-token')) {
          sessionStorage.removeItem(key);
        }
      });
      
      // Force clear state
      setUser(null);
      setIsAdmin(false);
      
      toast({
        title: "Logged out",
        description: "Session cleared successfully.",
      });
    }
  };

  const updateProfile = async (fullName: string) => {
    try {
      if (!user) {
        return { error: new Error('No user logged in') };
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ full_name: fullName })
        .eq('id', user.id);

      if (profileError) {
        toast({
          title: "Error",
          description: "Failed to update profile",
          variant: "destructive",
        });
        return { error: profileError };
      }

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      return { error: null };
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
      return { error };
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      if (!user) {
        return { error: new Error('No user logged in') };
      }

      // Re-authenticate
      const { error: reAuthError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: currentPassword,
      });

      if (reAuthError) {
        toast({
          title: "Error",
          description: "Current password is incorrect",
          variant: "destructive",
        });
        return { error: reAuthError };
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        toast({
          title: "Error",
          description: "Failed to update password",
          variant: "destructive",
        });
        return { error: updateError };
      }

      toast({
        title: "Success",
        description: "Password changed successfully. Please log in again.",
      });

      // Log out after password change
      await signOut();
      return { error: null };
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to update password",
        variant: "destructive",
      });
      return { error };
    }
  };

  const updateEmail = async (newEmail: string, password: string) => {
    try {
      if (!user) {
        return { error: new Error('No user logged in') };
      }

      // Re-authenticate
      const { error: reAuthError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password,
      });

      if (reAuthError) {
        toast({
          title: "Error",
          description: "Password is incorrect",
          variant: "destructive",
        });
        return { error: reAuthError };
      }

      // Update email
      const { error: updateError } = await supabase.auth.updateUser({
        email: newEmail,
      });

      if (updateError) {
        toast({
          title: "Error",
          description: "Failed to update email",
          variant: "destructive",
        });
        return { error: updateError };
      }

      // Update profiles table
      await supabase
        .from('profiles')
        .update({ email: newEmail })
        .eq('id', user.id);

      toast({
        title: "Success",
        description: "Verification email sent to new address. Please confirm to complete the change.",
      });

      return { error: null };
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to update email",
        variant: "destructive",
      });
      return { error };
    }
  };

  const getMFAStatus = async () => {
    try {
      if (!user) {
        return { enabled: false, error: new Error('No user logged in') };
      }

      // Check database status first (source of truth)
      const { data: dbStatus } = await supabase
        .from('user_mfa_status')
        .select('mfa_required, mfa_completed')
        .eq('user_id', user.id)
        .single();

      const dbMfaEnabled = dbStatus?.mfa_required && dbStatus?.mfa_completed;

      // Check Supabase Auth factors
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const hasAuthFactors = factors?.totp && factors.totp.length > 0;

      // If DB says disabled but Auth has factors, clean them up (auto-cleanup orphaned factors)
      if (!dbMfaEnabled && hasAuthFactors) {
        console.log('Cleaning up orphaned MFA factors');
        for (const factor of factors.totp || []) {
          await supabase.auth.mfa.unenroll({ factorId: factor.id });
        }
        return { enabled: false, error: null };
      }

      // Use DB status as source of truth
      return { enabled: !!dbMfaEnabled, error: null };
    } catch (error: any) {
      return { enabled: false, error };
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAdmin, 
      loading, 
      signIn, 
      signUp, 
      signOut,
      updateProfile,
      updatePassword,
      updateEmail,
      getMFAStatus,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
